import React, { FunctionComponent, useEffect, useMemo, useCallback, MouseEvent } from 'react';
import { IconButton, SearchIcon, Tooltip, useMediaQuery } from '@rbx/ui';
import {
  TopNavigation,
  AuthenticationStatus,
  useNavigationConfigs,
  AssistantTab,
} from '@rbx/creator-hub-navigation';
import { useRobloxAuthentication } from '@rbx/auth';
import { useTranslation } from '@rbx/intl';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import { assistantPathSuffix } from '@modules/assistant/constants/assistantNavConstants';
import useIsAssistantActive from '@modules/assistant/hooks/useIsAssistantActive';
import DocsSiteSource from '@modules/eventStream/enum/DocsSiteSource';
import usePageLogs from '@modules/miscellaneous/common/hooks/usePageLogs';
import LoggedPage from '@modules/eventStream/enum/LoggedPage';
import { useRouter } from 'next/router';
import { TestId } from '@modules/utilities/constants/testConstants';
import { topNavRightContentID } from '@modules/layout/layout/constants/layoutConstants';
import SearchContainer from '@modules/ui/search/SearchContainer';
import { getSearchKeyboardShortcut } from '@modules/utilities/utils/getKeyboardShortcut';
import { ESearchInteraction } from '@modules/eventStream/enum/DocsSiteSearch';
import useNavigationSearch from '../hooks/useNavigationSearch';
import PageNavigation from './PageNavigation';
import MobileSidebar from './MobileSidebar';
import useUnifiedTopNavigationStyles from './UnifiedTopNavigation.styles';
import NotificationTray from './NotificationTray';

const UnifiedTopNavigation: FunctionComponent<React.PropsWithChildren> = () => {
  const router = useRouter();
  const { logEnterEntryPoint } = usePageLogs(LoggedPage.Assistant);
  const { isAssistantActive } = useIsAssistantActive();
  const {
    user: authenticatedUser,
    isFetched: isAuthLoaded,
    logout,
    login,
  } = useRobloxAuthentication();
  const {
    classes: { searchButton, rightContent, bottomWrapper },
  } = useUnifiedTopNavigationStyles();
  // TODO UnifiedTop: get the breakpoints from UnifiedTopNavigation provider
  const showHamburgerMenu = useMediaQuery((theme) => theme.breakpoints.down('Large'));
  const { setSearchDialogOpenWithEvent } = useNavigationSearch();

  const { showSecondaryNavigation } = useAppConfig();
  const { translate } = useTranslation();
  const { toggleHomeDrawerOpen, toggleProductNavigationDrawer } = useNavigationConfigs();

  let pathname = '';

  if (typeof window !== 'undefined') {
    pathname = window.location.pathname;
  }

  const onSearchIconClick = useCallback(
    () =>
      setSearchDialogOpenWithEvent({
        searchDialogOpen: true,
        interaction: ESearchInteraction.NavSearchIcon,
      }),
    [setSearchDialogOpenWithEvent],
  );

  useEffect(() => {
    toggleHomeDrawerOpen(false);
    toggleProductNavigationDrawer(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const onAssistantClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      if (isAssistantActive) return;
      logEnterEntryPoint(DocsSiteSource.DocsTopNavAssistantButton);
      router.push(assistantPathSuffix);
    },
    [router, isAssistantActive, logEnterEntryPoint],
  );

  const secondaryTabs = useMemo(() => {
    return showSecondaryNavigation ? (
      <div className={bottomWrapper}>
        <PageNavigation />
      </div>
    ) : null;
  }, [showSecondaryNavigation, bottomWrapper]);

  return (
    <TopNavigation
      rightContent={
        <div
          id={topNavRightContentID}
          data-testid={TestId.TopNavRightContent}
          className={rightContent}>
          <AssistantTab persistent={!!authenticatedUser} onClick={onAssistantClick} />
          <Tooltip title={getSearchKeyboardShortcut(translate('Label.SearchDocs'))}>
            <IconButton
              className={searchButton}
              aria-label={translate('Label.openDialog') || 'open dialog'}
              size={showHamburgerMenu ? 'small' : 'medium'}
              color='secondary'
              onClick={onSearchIconClick}>
              <SearchIcon />
            </IconButton>
          </Tooltip>
          <NotificationTray />
          <AuthenticationStatus
            user={authenticatedUser}
            loading={!isAuthLoaded}
            onLogout={logout}
            onLogin={login}
          />
          <SearchContainer />
        </div>
      }
      compactProductSidebarNavigation={<MobileSidebar />}
      bottomContent={secondaryTabs}
    />
  );
};

export default UnifiedTopNavigation;
